import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { Link as LinkIcon } from "lucide-react";
import type { QlikConnection } from "@repo/shared";
import { useTranslation } from "react-i18next";
import {
  Button,
  DataTable,
  PageContent,
  PageHeader,
  PageShell,
  TableIdentity,
  localTablePage,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import { DensityToggle } from "@/components/density-toggle";
import { connectionsQueryOptions } from "@/features/qlik-connections/api";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";

export const Route = createFileRoute("/_authenticated/single-app/")({
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  component: SingleAppConnectionsPage,
});

const PAGE_SIZES = [10, 25, 50, 100];

function SingleAppConnectionsPage() {
  const { t } = useTranslation("single-app");
  const { density, setDensity } = useTableDensity();
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const connectionsQuery = useQuery(connectionsQueryOptions);
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  // The search lives in the URL; page, size and sort are the table's own.
  const [local, setLocal] = useState<DataTableState>({
    page: 1,
    pageSize: 50,
    query: "",
    filters: {},
    selection: [],
  });
  const state: DataTableState = { ...local, query: q ?? "" };
  const onStateChange = useCallback(
    (next: DataTableState) => {
      setLocal(next);
      if (next.query !== (q ?? "")) {
        navigate({ search: (prev) => ({ ...prev, q: next.query || undefined }), replace: true });
      }
    },
    [navigate, q],
  );

  const columns: DataTableColumn<QlikConnection>[] = [
    {
      id: "name",
      header: t("index.table.name"),
      variant: "identity",
      rowHeader: true,
      rowLink: true,
      sort: "text",
      value: (c) => c.name,
      cell: (c) => (
        <TableIdentity
          leading={<LinkIcon />}
          name={
            <Link
              to="/single-app/$connectionId/apps"
              params={{ connectionId: c.id }}
              search={{
                q: undefined,
                spaces: undefined,
                owners: undefined,
                status: undefined,
                access: undefined,
              }}
              className="truncate font-medium text-ink hover:underline"
            >
              {c.name}
            </Link>
          }
        />
      ),
    },
    {
      id: "url",
      header: t("index.table.url"),
      sort: "text",
      value: (c) => c.baseUrl,
      cell: (c) => (
        <span className="font-mono text-xs text-dim">
          {c.baseUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
        </span>
      ),
    },
  ];

  const page = localTablePage(connectionsQuery.data ?? [], state, {
    columns,
    // By name, as before the migration (not the URL nor the other columns).
    search: (connection) => connection.name,
  });

  return (
    <PageShell
      header={<PageHeader title={t("index.pageTitle")} description={t("index.description")} />}
    >
      <PageContent fill>
        <DataTable
          label={t("index.pageTitle")}
          columns={columns}
          rows={page.rows}
          getRowId={(c) => c.id}
          state={state}
          onStateChange={onStateChange}
          density={density}
          loading={connectionsQuery.isPending}
          refreshing={listRefreshing(connectionsQuery)}
          error={connectionsQuery.isError ? { title: t("index.loadFailed") } : null}
          onRetry={() => void connectionsQuery.refetch()}
          search={{
            ...tableSearch(t("index.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
            debounceMs: 300,
          }}
          toolbar={<DensityToggle value={density} onChange={setDensity} />}
          pagination={{
            kind: "offset",
            total: page.total,
            pageSizeOptions: PAGE_SIZES,
            labels: paginationLabels(),
          }}
          messages={tableMessages({
            empty: {
              title: t("index.noConnections.title"),
              description: t("index.noConnections.description"),
              action: (
                <Button asChild>
                  <Link to="/connections/new">{t("index.noConnections.action")}</Link>
                </Button>
              ),
            },
            noResults: {
              title: t("index.noMatch.title"),
              description: t("index.noMatch.description", { query: state.query }),
              action: (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onStateChange(updateTableState(state, { query: "" }))}
                >
                  {t("index.noMatch.clearSearch")}
                </Button>
              ),
            },
          })}
        />
      </PageContent>
    </PageShell>
  );
}
